/**
 * Responsibility: the things a person typed, read from Firestore rather than
 * from the hub.
 *
 * **The durable half.** Everything here is something somebody entered and
 * cannot get back: holdings, likes, saved searches, topics, binned cards,
 * notification channels, settings. It is in Firestore precisely so that losing
 * the Oracle box loses the analysis and not the data - and these hooks are what
 * make that true in the browser as well as on paper.
 *
 * The regenerable half is deliberately not here. Search results, discovery
 * boards, catalogue prices and news all need a processor to produce them, and
 * no amount of caching makes them available when there is none. Those stay on
 * the hub, and the page says when they are unavailable rather than pretending.
 *
 * Money arrives as text and stays as text until something formats it. Firestore
 * numbers are doubles and `19.99` stored as one is not `19.99` coming back,
 * which is the same rule the hub and the rules file follow.
 */

import { useAuth } from '../auth/authContext';

import {
  BINS,
  CHANNELS,
  HISTORY,
  HOLDINGS,
  LIKES,
  SEARCHES,
  SETTINGS,
  SETTINGS_DOC,
  SUMMARY,
  SUMMARY_DOC,
  TOPICS,
  documentPath,
  subcollection,
} from './paths';
import { useLiveCollection, useLiveDocument, type Live, type WithId } from './useLive';

/** A holding as Firestore stores it. Money is text; see the module note. */
export interface StoredHolding {
  product_key: string;
  name: string;
  category: string;
  url: string;
  image_url?: string | null;
  grading_company?: string | null;
  grade_label: string;
  cert_number?: string | null;
  quantity: number;
  cost_basis?: string | null;
  latest_value?: string | null;
  notes: string;
  added_at: { seconds: number } | null;
}

export interface StoredDailyTotal {
  total: string;
  holdings: number;
}

export interface StoredSummary {
  item_count: number;
  card_count: number;
  total_value: string;
  total_cost: string;
  total_gain?: string | null;
  change_1d?: string | null;
  change_30d?: string | null;
  change_90d?: string | null;
  tracked_days: number;
}

export interface StoredLike {
  product_key: string;
  name: string;
  category: string;
  url: string;
  image_url?: string | null;
  baseline_value?: string | null;
  baseline_label?: string | null;
  notes: string;
}

export interface StoredSearch {
  label: string;
  query: string;
  mode: string;
  category?: string | null;
  result_count?: number | null;
}

export interface StoredTopic {
  phrase: string;
  normalised: string;
  notify_at: string;
  muted: boolean;
}

export interface StoredSettings {
  grading_cost_per_card: string;
  grading_submission_shipping: string;
  grading_sell_fee_rate: string;
  grading_cards_bought: number;
}

/** The signed-in uid, or null - which every hook below treats as "not yet". */
function useUid(): string | null {
  return useAuth().user?.uid ?? null;
}

function path(uid: string | null, name: string): string | null {
  return uid ? subcollection(uid, name) : null;
}

/**
 * Everything owned, newest first.
 *
 * Ordered by `added_at`, which is one of the few fields left indexed on
 * purpose - see `firestore.indexes.json`, where most are exempted because
 * nothing queries them and an index entry carries a copy of the value.
 */
export function useLiveHoldings(): Live<WithId<StoredHolding>[]> {
  return useLiveCollection<StoredHolding>(path(useUid(), HOLDINGS), {
    orderByField: 'added_at',
    descending: true,
    max: 1000,
  });
}

/**
 * The value curve, oldest last.
 *
 * Read by document id, which is the date - so ordering costs nothing and the
 * most recent two years come back without a field index.
 */
export function useLiveHistory(days = 730): Live<WithId<StoredDailyTotal>[]> {
  return useLiveCollection<StoredDailyTotal>(path(useUid(), HISTORY), {
    descending: true,
    max: days,
  });
}

/** The figures across the top, computed by the hub and written down. */
export function useLiveSummary(): Live<StoredSummary> {
  const uid = useUid();
  return useLiveDocument<StoredSummary>(uid ? documentPath(uid, SUMMARY, SUMMARY_DOC) : null);
}

export function useLiveLikes(): Live<WithId<StoredLike>[]> {
  return useLiveCollection<StoredLike>(path(useUid(), LIKES), { max: 1000 });
}

export function useLiveSearches(): Live<WithId<StoredSearch>[]> {
  return useLiveCollection<StoredSearch>(path(useUid(), SEARCHES), { max: 200 });
}

export function useLiveTopics(): Live<WithId<StoredTopic>[]> {
  return useLiveCollection<StoredTopic>(path(useUid(), TOPICS), { max: 200 });
}

export function useLiveBins(): Live<WithId<Record<string, unknown>>[]> {
  return useLiveCollection(path(useUid(), BINS), { max: 500 });
}

/**
 * Notification channels, in their masked form.
 *
 * The real Apprise URL is a live credential and lives in `secrets`, which the
 * rules deny to every client including its owner. There is nothing a browser
 * needs it for, and a token leaking from a laptop should not carry a push
 * credential with it.
 */
export function useLiveChannels(): Live<WithId<Record<string, unknown>>[]> {
  return useLiveCollection(path(useUid(), CHANNELS), { max: 100 });
}

export function useLiveSettings(): Live<StoredSettings> {
  const uid = useUid();
  return useLiveDocument<StoredSettings>(uid ? documentPath(uid, SETTINGS, SETTINGS_DOC) : null);
}
